import { ComponentType, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import * as authenticationService from '../../data-layer/authentication/service';
import {
    removeCollectionAsync,
    shareCollectionAsync,
    updateCollectionReactionAsync,
} from '../../data-layer/collections/actions';
import { CollectionModel, CollectionStatus } from '../../data-layer/collections/types';
import { useDispatch } from '../../data-layer/useDispatch';
import { ymTrackOpenCollectionFromProfile } from '../../utils/metrika';
import { useOpenDialog } from '../ModalRoute';
import { DIALOGS } from '../constants';
import { useCurrentUser } from './useCurrentUser';

interface WithCollectionCallbacksProps {
    onRemove?: VoidFunction;
    onEdit?: VoidFunction;
    onShare?: VoidFunction;
    onReport?: VoidFunction;
    onReaction?: VoidFunction;
    onMove?: VoidFunction;
}

export function withCollectionCallbacks<T>(
    BaseComponent: ComponentType<T & WithCollectionCallbacksProps>,
): ComponentType<
    Omit<Pick<T, Exclude<keyof T, keyof WithCollectionCallbacksProps>>, 'collection'> & {
        collection: CollectionModel;
    }
> {
    return (props) => {
        const { collection, ...other } = props;
        const dispatch = useDispatch();
        const navigate = useNavigate();
        const currentUser = useCurrentUser();

        const openDialog = useOpenDialog();
        const onEdit = useCallback(
            () => openDialog(DIALOGS.EDIT_COLLECTION, { collectionId: collection.id }),
            [collection, openDialog],
        );
        const onRemove = useCallback(async () => {
            await dispatch(removeCollectionAsync(collection.id));
            navigate('/profile');
        }, [dispatch, collection.id, navigate]);

        const onShare = useCallback(() => {
            if (
                collection.status === CollectionStatus.MODERATION ||
                collection.status === CollectionStatus.DECLINED
            ) {
                openDialog(DIALOGS.COLLECTION_STATUS, { collection: collection });
            } else {
                dispatch(
                    shareCollectionAsync(
                        collection,
                        currentUser?.login,
                        currentUser?.profile?.publicId,
                    ),
                );
            }
        }, [collection, currentUser?.login, currentUser?.profile?.publicId, dispatch]);

        const onReport = useCallback(() => {
            openDialog(DIALOGS.REPORT_DIALOG, {
                targetId: collection.id,
                targetType: 'collection',
            });
        }, [collection.id]);

        const onReaction = useCallback(
            authenticationService.protect(() =>
                dispatch(
                    updateCollectionReactionAsync({
                        collectionId: collection.id,
                        login: currentUser?.login,
                        businessId: currentUser?.profile?.publicId,
                        isPublic: collection.isPublic,
                        own: collection.modifiable,
                    }),
                ),
            ),
            [
                collection.id,
                collection.isPublic,
                collection.modifiable,
                dispatch,
                currentUser?.login,
                currentUser?.profile?.publicId,
            ],
        );

        const onMove = useCallback(() => {
            ymTrackOpenCollectionFromProfile({
                page: 'Профиль пользователя',
                userLogin: currentUser?.login,
                businessId: currentUser?.profile?.publicId,
                isPublic: collection.isPublic,
                collectionId: collection.id,
                own: true,
            });
        }, [
            collection.isPublic,
            collection.modifiable,
            collection.id,
            currentUser?.login,
            currentUser?.profile?.publicId,
        ]);

        return (
            // @ts-ignore
            <BaseComponent
                {...other}
                collection={collection}
                onRemove={onRemove}
                onEdit={onEdit}
                onShare={onShare}
                onReport={onReport}
                onReaction={onReaction}
                onMove={onMove}
            />
        );
    };
}
